import { ClientScoringStatus, ScoringCategory, ScoringSupplier } from '../../../../types/advise';
import { TextSpan } from '../../../Atoms/Text';
import { TextButton } from '../../../Atoms/TextButton';
import { StatusChip, StatusChipProps } from '../EngagementSteps';
import { categoryScore, overallScore, SCORE_COLORS, scoreTone } from './SupplierScoring.calc';

// A client's finished scoring reads "Completed" in solid green, unlike a finished step's outlined "Complete".
const CLIENT_SCORING_CHIPS: Record<ClientScoringStatus, StatusChipProps> = {
  not_started: { status: 'not_started' },
  in_progress: { status: 'in_progress' },
  completed: { status: 'complete', label: 'Completed', variant: 'filled' }
};

interface SupplierScoreCardProps {
  supplier: ScoringSupplier;
  categories: ScoringCategory[];
  ratings: Map<string, number>;
  active: boolean;
  onSelect: () => void;
  // Left out for someone who can only view the engagement.
  onRequestScoring?: () => void;
}

/** A shortlisted supplier's overall and category scores; selecting it shows its ratings and changes none. */
export const SupplierScoreCard = ({
  supplier,
  categories,
  ratings,
  active,
  onSelect,
  onRequestScoring
}: SupplierScoreCardProps) => {
  const overall = overallScore(categories, supplier.id, ratings);
  return (
    <div
      className={`flex w-64 shrink-0 flex-col gap-2 rounded-lg border bg-white p-3 ${
        active ? 'border-2 border-main-100' : 'border-grey-2'
      }`}>
      <button
        type="button"
        aria-pressed={active}
        onClick={onSelect}
        className={`flex w-full flex-col gap-2 text-left ${active ? '' : 'opacity-60'}`}>
        <span className="flex w-full items-center justify-between gap-2">
          <TextSpan
            weight="semibold"
            color={active ? 'indigo' : 'grey8'}>
            {supplier.supplier_name}
          </TextSpan>
          <StatusChip {...CLIENT_SCORING_CHIPS[supplier.status]} />
        </span>
        <span className="flex flex-col items-center">
          <TextSpan
            size="xxl"
            weight="semibold"
            color={SCORE_COLORS[scoreTone(overall)]}>
            {overall ?? '–'}
          </TextSpan>
          <TextSpan
            size="xs"
            color="grey6">
            Overall Score
          </TextSpan>
        </span>
        <span className="flex w-full justify-between gap-1">
          {categories.map((category) => {
            const { average } = categoryScore(category, supplier.id, ratings);
            return (
              <span
                key={category.id}
                className="flex flex-col items-center">
                <TextSpan
                  size="xs"
                  color="grey6">
                  {category.short_label}
                </TextSpan>
                <TextSpan
                  size="sm"
                  weight="medium"
                  color={SCORE_COLORS[scoreTone(average)]}>
                  {average ?? '–'}
                </TextSpan>
              </span>
            );
          })}
        </span>
      </button>
      {onRequestScoring && (
        <TextButton
          size="xs"
          className="self-start"
          onClick={onRequestScoring}>
          Request Supplier Scoring →
        </TextButton>
      )}
    </div>
  );
};
